import { useState, useEffect, useCallback, useRef } from 'react'
import { kanbify } from '../lib/kanbifyClient'

function formatElapsed(seconds) {
    const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0))
    const h = Math.floor(safeSeconds / 3600)
    const m = Math.floor((safeSeconds % 3600) / 60)
    const s = safeSeconds % 60
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// Module-level deduplication. The Sidebar is rendered TWICE (mobile drawer +
// desktop sidebar), and React StrictMode in dev doubles every effect — so
// without dedup, get_active_session fires 4× per dashboard load.
// We cache an in-flight promise for 2s so concurrent / near-concurrent calls
// share a single network roundtrip.
let activeSessionPromise = null
let activeSessionExpiry = 0

function fetchActiveSessionDeduped() {
    const now = Date.now()
    if (activeSessionPromise && now < activeSessionExpiry) {
        return activeSessionPromise
    }
    activeSessionPromise = kanbify.rpc('get_active_session').then((res) => {
        // Keep the resolved value cached for 2 more seconds for any near-simultaneous callers
        activeSessionExpiry = Date.now() + 2000
        return res
    }).catch((err) => {
        activeSessionPromise = null
        throw err
    })
    activeSessionExpiry = now + 2000
    return activeSessionPromise
}

export function invalidateActiveSessionCache() {
    activeSessionPromise = null
    activeSessionExpiry = 0
}

export function usePontaj(userId) {
    const [isActive, setIsActive] = useState(false)
    const [checkedInAt, setCheckedInAt] = useState(null)
    const [elapsed, setElapsed] = useState('00:00')
    const tickRef = useRef(null)

    const startTick = useCallback((from) => {
        clearInterval(tickRef.current)
        const updateElapsed = () => {
            const fromMs = new Date(from).getTime()
            const secs = Number.isFinite(fromMs)
                ? Math.max(0, Math.floor((Date.now() - fromMs) / 1000))
                : 0
            setElapsed(formatElapsed(secs))
        }
        updateElapsed()
        tickRef.current = setInterval(updateElapsed, 1000)
    }, [])

    const fetchSession = useCallback(async () => {
        if (!userId) return
        const { data } = await fetchActiveSessionDeduped()
        if (data?.active) {
            setIsActive(true)
            setCheckedInAt(data.checked_in_at)
            startTick(data.checked_in_at)
        } else {
            setIsActive(false)
            setCheckedInAt(null)
            setElapsed('00:00')
            clearInterval(tickRef.current)
        }
    }, [userId, startTick])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- initial session sync on mount; fetchSession is memoized
        fetchSession()
        const refreshTimer = setInterval(() => {
            invalidateActiveSessionCache()
            void fetchSession()
        }, 60000)
        const handlePontifySync = () => {
            invalidateActiveSessionCache()
            void fetchSession()
        }
        window.addEventListener('kanbify:pontify-sync-complete', handlePontifySync)
        return () => {
            clearInterval(tickRef.current)
            clearInterval(refreshTimer)
            window.removeEventListener('kanbify:pontify-sync-complete', handlePontifySync)
        }
    }, [fetchSession])

    const clockIn = useCallback(async () => {
        const { data, error } = await kanbify.rpc('clock_in')
        if (!error && data?.success) {
            invalidateActiveSessionCache()
            await fetchSession()
        }
        return { data, error }
    }, [fetchSession])

    const clockOut = useCallback(async () => {
        const { data, error } = await kanbify.rpc('clock_out')
        if (!error && data?.success) {
            invalidateActiveSessionCache()
            setIsActive(false)
            setCheckedInAt(null)
            setElapsed('00:00')
            clearInterval(tickRef.current)
        }
        return { data, error }
    }, [])

    return { isActive, checkedInAt, elapsed, clockIn, clockOut }
}
